import { Amplify } from "aws-amplify";
import { signInWithRedirect, signOut } from "@aws-amplify/auth";
import { getClaimsOrigin } from "../utils/claimsPortal";
import {
  AWS_REGION,
  COGNITO_DOMAIN,
  REDIRECT_SIGNIN_URL,
  REDIRECT_SIGNOUT_URL,
  USER_POOL_CLIENT_ID,
  USER_POOL_ID,
} from "../services/config";

Amplify.configure({
  Auth: {
    region: AWS_REGION,
    Cognito: {
      userPoolId: USER_POOL_ID,
      userPoolClientId: USER_POOL_CLIENT_ID,
      loginWith: {
        oauth: {
          domain: COGNITO_DOMAIN,
          scopes: ["openid", "email", "profile"],
          redirectSignIn: [REDIRECT_SIGNIN_URL],
          redirectSignOut: [REDIRECT_SIGNOUT_URL],
          responseType: "code",
        },
      },
    },
  },
} as any);

export const signIn = () => {
  return signInWithRedirect();
};

export const doSignOut = async () => {
  await signOut();
};

/** Where we fall back to when the Cognito logout endpoint cannot be built. */
export const LOGGED_OUT_PATH = "/logged-out";

/**
 * The Cognito hosted logout endpoint for this app client.
 *
 * Ending the *hosted* session is what makes a logout stick: without it Cognito
 * still holds a session cookie and the next `signInWithRedirect()` hands back a
 * fresh code with no credential prompt, so the user looks signed in again and
 * logs out a second time (ICE-BC-BUG-018).
 *
 * We build and navigate to this URL ourselves rather than relying on Amplify's
 * implicit redirect, because `signOut()` resolves as soon as it *assigns*
 * `window.location`, not once the browser has left — which left a window for
 * other code to abort the uncommitted navigation.
 *
 * `logout_uri` is where Cognito returns the browser once the hosted session is
 * gone. We derive it as `<claims origin>/logged-out` rather than using
 * `REDIRECT_SIGNOUT_URL`, because that env value is the **origin root** in
 * staging and production — so Cognito sent the user to `/`, which renders the
 * client-facing `WelcomePage` (ICE-BC-BUG-008). That is the flash of the
 * signed-out screen followed by the public site.
 *
 * `<origin>/logged-out` is already a registered sign-out URL on every
 * environment's app client (verified against local, staging and production —
 * production is a separate pool), so this needs no Cognito change and no CI
 * secret update. Deriving it also avoids the registered values disagreeing on a
 * trailing slash: production registers `https://claims.icebolethu.co.za` and
 * rejects the `/` variant.
 *
 * The origin comes from `getClaimsOrigin()` (the canonical claims origin behind
 * `REDIRECT_SIGNIN`) rather than `window.location.origin`, so a sign-out from a
 * non-canonical host still returns to a registered URL. If the Cognito env is
 * missing we fail soft to the local logged-out screen rather than navigating to
 * a malformed `https://undefined/logout`.
 */
export const getHostedLogoutUrl = (): string => {
  if (!COGNITO_DOMAIN || !USER_POOL_CLIENT_ID) {
    return LOGGED_OUT_PATH;
  }

  const origin = getClaimsOrigin();
  const logoutTarget = origin
    ? `${origin}${LOGGED_OUT_PATH}`
    : REDIRECT_SIGNOUT_URL;

  if (!logoutTarget) {
    return LOGGED_OUT_PATH;
  }

  const domain = COGNITO_DOMAIN.replace(/^https?:\/\//, "").replace(/\/+$/, "");
  const clientId = encodeURIComponent(USER_POOL_CLIENT_ID);
  const logoutUri = encodeURIComponent(logoutTarget);
  return `https://${domain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
};
